import type { PluginListenerHandle } from '@capacitor/core'
import { Network } from '@capacitor/network'
import { BellRing, CheckSquare, CircleHelp, Home, Info, Settings, type LucideIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useLocalStorage } from './hooks/useLocalStorage'
import OfficialSources from './components/OfficialSources'
import { initializeAdMobBanner } from './lib/ads'
import { loadContent } from './lib/content'
import { isAllowedOfficialUrl, openOfficialUrl } from './lib/externalLinks'
import {
  isNativeApp,
  notificationStorageKey,
  syncNativeNotificationsIfPermitted,
  type NotificationSettings,
} from './lib/notifications'
import type { ContentBundle } from './types/content'
import FaqView from './views/FaqView'
import GuideView from './views/GuideView'
import HomeView from './views/HomeView'
import PrepareView from './views/PrepareView'
import SettingsView from './views/SettingsView'

type AppTab = 'home' | 'prepare' | 'guide' | 'faq' | 'settings'

interface TabDefinition {
  id: AppTab
  label: string
  icon: LucideIcon
}

const tabs: TabDefinition[] = [
  { id: 'home', label: '홈', icon: Home },
  { id: 'prepare', label: '신청준비', icon: CheckSquare },
  { id: 'guide', label: '지원안내', icon: Info },
  { id: 'faq', label: 'FAQ·소식', icon: CircleHelp },
  { id: 'settings', label: '설정', icon: Settings },
]

const tabTitles: Record<AppTab, string> = {
  home: '홈',
  prepare: '신청 준비',
  guide: '지원 안내',
  faq: 'FAQ·변경 소식',
  settings: '설정',
}

export default function App() {
  const [content, setContent] = useState<ContentBundle | null>(null)
  const [loadingError, setLoadingError] = useState(false)
  const [isOffline, setIsOffline] = useState(false)
  const [tab, setTab] = useState<AppTab>('home')
  const [hasSeenIntro, setHasSeenIntro] = useLocalStorage('gg-transport-intro-seen', false)
  const [isIntroTapGuardActive, setIsIntroTapGuardActive] = useState(false)
  const externalLinkGuardUntil = useRef(0)
  const introTapGuardTimer = useRef<number | undefined>(undefined)

  useEffect(() => {
    return () => window.clearTimeout(introTapGuardTimer.current)
  }, [])

  useEffect(() => {
    let active = true
    loadContent()
      .then((bundle) => {
        if (active) setContent(bundle)
      })
      .catch(() => {
        if (active) setLoadingError(true)
      })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (!isNativeApp()) return
    const handleExternalLink = (event: MouseEvent) => {
      if (Date.now() < externalLinkGuardUntil.current) {
        event.preventDefault()
        return
      }
      const target = event.target
      if (!(target instanceof Element)) return
      const anchor = target.closest('a[target="_blank"]')
      if (!(anchor instanceof HTMLAnchorElement) || !isAllowedOfficialUrl(anchor.href)) return
      event.preventDefault()
      void openOfficialUrl(anchor.href)
    }
    document.addEventListener('click', handleExternalLink)
    return () => document.removeEventListener('click', handleExternalLink)
  }, [])

  useEffect(() => {
    let nativeListener: PluginListenerHandle | undefined
    const updateBrowserStatus = () => setIsOffline(!navigator.onLine)

    if (isNativeApp()) {
      void Network.getStatus().then((status) => setIsOffline(!status.connected))
      void Network.addListener('networkStatusChange', (status) => setIsOffline(!status.connected)).then((handle) => {
        nativeListener = handle
      })
      return () => {
        void nativeListener?.remove()
      }
    }

    updateBrowserStatus()
    window.addEventListener('online', updateBrowserStatus)
    window.addEventListener('offline', updateBrowserStatus)
    return () => {
      window.removeEventListener('online', updateBrowserStatus)
      window.removeEventListener('offline', updateBrowserStatus)
    }
  }, [])

  useEffect(() => {
    if (!content || !isNativeApp()) return
    try {
      const stored = localStorage.getItem(notificationStorageKey)
      if (!stored) return
      const settings = JSON.parse(stored) as NotificationSettings
      void syncNativeNotificationsIfPermitted(content.program.application, settings)
    } catch {
      // Invalid local settings are ignored and can be reset from the settings tab.
    }
  }, [content])

  useEffect(() => {
    if (!content || !hasSeenIntro || !isNativeApp()) return
    void initializeAdMobBanner().catch(() => {
      // 광고 초기화 실패는 핵심 정보 기능의 사용을 방해하지 않습니다.
    })
  }, [content, hasSeenIntro])

  const switchTab = (nextTab: AppTab) => {
    setTab(nextTab)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const completeIntro = () => {
    externalLinkGuardUntil.current = Date.now() + 750
    setIsIntroTapGuardActive(true)
    setHasSeenIntro(true)
    window.clearTimeout(introTapGuardTimer.current)
    introTapGuardTimer.current = window.setTimeout(() => setIsIntroTapGuardActive(false), 750)
  }

  if (!content) {
    return (
      <main className="loading-screen">
        <div className="brand-mark"><BellRing aria-hidden="true" /></div>
        <h1>경기어린이청소년교통비알리미</h1>
        <p>{loadingError ? '기본 정보를 불러오지 못했습니다. 다시 실행해 주세요.' : '검수된 지원 정보를 불러오고 있습니다.'}</p>
      </main>
    )
  }

  if (!hasSeenIntro) {
    return (
      <main className="intro-screen">
        <section className="intro-dialog" aria-labelledby="intro-title">
          <div className="brand-mark"><BellRing aria-hidden="true" /></div>
          <p className="eyebrow">시작하기 전에</p>
          <h1 id="intro-title">경기어린이청소년<br />교통비알리미</h1>
          <p>{content.program.disclaimer}</p>
          <div className="intro-source"><strong>공식 정보 출처</strong><span>경기도청 · 경기교통공사</span></div>
          <button type="button" className="button button--primary" onClick={completeIntro}>확인하고 시작하기</button>
          <a href={content.program.sourceUrl} target="_blank" rel="noreferrer">공식 출처 먼저 보기</a>
        </section>
      </main>
    )
  }

  return (
    <div className="app-shell">
      {isIntroTapGuardActive && <div className="intro-tap-guard" aria-hidden="true" />}
      <header className="app-header">
        <div>
          <span className="app-header__brand">경기어린이청소년교통비알리미</span>
          <h1>{tabTitles[tab]}</h1>
        </div>
        <span className="unofficial-badge">비공식 정보 앱</span>
      </header>

      {(isOffline || content.hasRefreshIssue) && (
        <div className="offline-banner" role="status">
          {isOffline ? '네트워크가 연결되지 않아 저장된 검수 정보를 표시합니다.' : '최신 정보를 확인하지 못해 저장된 검수 정보를 표시합니다.'}
        </div>
      )}

      <aside className="government-notice" aria-label="비공식 앱 안내">
        <strong>정부 기관의 공식 앱이 아닙니다.</strong>
        <span>경기도청 원문에서 지원 정보를 최종 확인하세요.</span>
        <a href={content.program.sourceUrl} target="_blank" rel="noreferrer">경기도청 공식 원문</a>
      </aside>

      <main id="main-content" className="app-main">
        {tab === 'home' && <HomeView program={content.program} onOpenPrepare={() => switchTab('prepare')} onOpenSettings={() => switchTab('settings')} />}
        {tab === 'prepare' && <PrepareView program={content.program} onOpenSettings={() => switchTab('settings')} />}
        {tab === 'guide' && <GuideView program={content.program} />}
        {tab === 'faq' && <FaqView faq={content.faq} notices={content.notices} />}
        {tab === 'settings' && <SettingsView program={content.program} />}
        <OfficialSources disclaimer={content.program.disclaimer} sources={content.program.officialSources} />
      </main>

      <nav className="bottom-nav" aria-label="주요 메뉴">
        {tabs.map((item) => {
          const Icon = item.icon
          const selected = tab === item.id
          return (
            <button key={item.id} type="button" aria-current={selected ? 'page' : undefined} onClick={() => switchTab(item.id)}>
              <Icon aria-hidden="true" size={21} />
              <span>{item.label}</span>
            </button>
          )
        })}
      </nav>
    </div>
  )
}
